import { ArrowRight } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../../lib/cn';
import { soVN } from '../../../lib/dinh-dang';

/**
 * Primitive riêng của dashboard (§9.7).
 *
 * Dashboard có trước `shared/Bento` nên tự định nghĩa bộ primitive gần giống.
 * Khác biệt đáng giữ: con số dùng `text-3xl font-bold` thay vì `text-xl
 * font-semibold` — đây là **nhấn mạnh có chủ đích** của màn hạ cánh, không phải
 * lỗi (§17.4 mục 5).
 */

export function Tile({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn('rounded-lg border border-default bg-surface shadow-sm', className)}>
      {children}
    </div>
  );
}

/** Lưu ý: `items-center`, khác `shared/Bento` dùng `items-start`. */
export function TileHeader({
  icon,
  title,
  action,
}: {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-default px-5 pb-3 pt-4">
      <div className="flex min-w-0 items-center gap-2">
        {icon}
        <h2 className="truncate text-sm font-semibold text-content-primary">{title}</h2>
      </div>
      {action}
    </div>
  );
}

export function StatRow({
  label,
  value,
  accent,
}: {
  label: string;
  value: ReactNode;
  accent?: string;
}) {
  return (
    <div className="flex items-center justify-between border-b border-default py-2 last:border-0">
      <span className="text-sm text-content-secondary">{label}</span>
      <span className={cn('text-sm font-bold tabular-nums', accent ?? 'text-content-primary')}>
        {value}
      </span>
    </div>
  );
}

/**
 * Thanh ngang — primitive biểu đồ duy nhất của màn này.
 * Dùng bậc `-500` vì thanh chỉ cần tương phản 3:1, không phải 4.5:1 (§5.1).
 */
export function HBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="h-2 flex-1 overflow-hidden rounded-full bg-subtle">
      <div className={cn('h-full rounded-full transition-all', color)} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function MoreLink({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-medium text-primary-600 transition-colors hover:text-primary-700"
    >
      {children}
      <ArrowRight size={12} />
    </button>
  );
}

export function EmptyHint({ children }: { children: ReactNode }) {
  return <p className="py-6 text-center text-xs text-content-muted">{children}</p>;
}

/** Lỗi của riêng một ô: nói rõ cách xử lý, đặt ngay trong ô sai. */
export function ErrorHint({
  message,
  retryLabel,
  onRetry,
}: {
  message: string;
  retryLabel: string;
  onRetry: () => void;
}) {
  return (
    <div role="alert" className="flex flex-col items-center gap-2 py-6 text-center">
      <p className="text-xs text-danger-700">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded-lg border border-default px-3 py-1 text-xs font-medium text-content-secondary transition-colors hover:bg-subtle"
      >
        {retryLabel}
      </button>
    </div>
  );
}

/** Tông chip biểu tượng của `MetricTile`: chỉ 4 tông, dùng bậc -600. */
const METRIC_TONE: Record<string, string> = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  violet: 'bg-violet-50 text-violet-600',
  teal: 'bg-teal-50 text-teal-600',
};

export function MetricTile({
  label,
  value,
  sub,
  icon,
  tone,
  loading,
  locale,
}: {
  label: string;
  value: number;
  sub: string;
  icon: ReactNode;
  tone: keyof typeof METRIC_TONE | string;
  loading: boolean;
  locale: string;
}) {
  return (
    <Tile className="col-span-12 min-h-[118px] sm:col-span-6 lg:col-span-3">
      <div className="flex items-center justify-between gap-3 px-5 py-4">
        <div className="min-w-0">
          <p className="truncate text-sm font-medium text-content-secondary">{label}</p>
          {/* Lúc đang tải thì hiện `—`, không dùng skeleton: khung ô vẫn giữ nguyên. */}
          <p className="mt-2 text-3xl font-bold leading-none tabular-nums text-content-primary">
            {loading ? '—' : soVN(value, locale)}
          </p>
          <p className="mt-1.5 truncate text-xs text-content-muted">{sub}</p>
        </div>
        <span
          className={cn(
            'grid h-11 w-11 shrink-0 place-items-center rounded',
            METRIC_TONE[tone] ?? METRIC_TONE['primary'],
          )}
        >
          {icon}
        </span>
      </div>
    </Tile>
  );
}
